import { createContext, useContext, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { getUserEmail } from 'ducks/auth/helpers';
import { useAuthManager } from 'redesign/components/common/contexts/AuthManager';
import { CompanyName } from 'redesign/constants/config';
import { AgreementGlobalPermission } from 'redesign/hooks/permissions/useAgreementGlobalPermissions';
import { Config, ConfigContext } from 'redesign/typings/config';
import AuthClient from 'services/api/auth_client';

const emptyAgreementsPermissions: Record<AgreementGlobalPermission, boolean> = {
    activate_permission: false,
    edit_permission: false,
    create_permission: false,
    create_if_active_permission: false,
    request_validation: false,
    review_permission: false,
    deactivate_permission: false,
    default_discount_permission: false,
    renew_permission: false,
    renew_if_active_permission: false,
    edit_sync_field_permission: false,
    edit_owner_field_permission: false,
    edit_auto_deactivate_permission: false,
    duplicate_permission: false,
    import_permission: false,
    add_restricted_price_list_permission: false,
};

export const emptyConfig: Config = {
    hasLoaded: false,
    company: CompanyName.EMPTY,
    filtersKey: '',
    catalog: {
        columns: {},
        views_filters: {},
        views_config: {},
        views_filters_default: {},
    },
    global_product_catalog: {
        enabled: undefined,
    },
    export: {
        PriceListItems: { exportModalisAllowed: false, fileName: 'export', availableFormats: [] },
    },
    users_page: {
        enabled: true,
    },
    settings: {
        tabs: [],
    },
    negotiate: {
        agreements: {
            standard: emptyAgreementsPermissions,
            shared: emptyAgreementsPermissions,
            master: emptyAgreementsPermissions,
        },
        bulk_update_owner_permission: false,
    },
    login_preview_images: [],
    tenant_type: 'Seller',
    divisions: { enabled: false, can_multi_select: false },
    competitorsConfig: {
        active: false,
        competitors: [],
        showCatalogColumn: false,
        isNew: false,
        ownCompetitorName: '',
    },
    stocksConfig: {
        active: false,
        isShownInTooltip: false,
    },
    sisenseConfig: { host: null, dashboard_id: null },
    reserved_column_names: [],
};

export const configContext = createContext<ConfigContext>({
    config: emptyConfig,
    fetchConfig: () => undefined,
});

export const useConfigContext = () => useContext(configContext);

const ConfigManager = ({ children }: { children: JSX.Element }) => {
    const [config, setConfig] = useState(emptyConfig);
    const { authData, isAuthenticated } = useAuthManager();
    const client = new AuthClient();
    const fetchConfig = async () => {
        const response = await client.getConfig();
        setConfig({
            ...response,
            hasLoaded: true,
            filtersKey: `_DASHBOARD_FILTERS_${response?.company}`,
        });
        Sentry.setTag('tenant_name', response?.tenant_name);
        Sentry.setUser({ email: getUserEmail() });
        return response;
    };

    useEffect(() => {
        if (isAuthenticated()) {
            fetchConfig();
        }
        // only if token changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData?.token]);

    return (
        <configContext.Provider
            value={{
                config,
                fetchConfig,
            }}
        >
            {children}
        </configContext.Provider>
    );
};

export default ConfigManager;
